import {
  ApolloClient,
  from,
  HttpLink,
  NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { ResultCodes } from "@app/service/resultCodes";

import { ConfigServiceInterface } from "@natera/platform/lib/service/config";

import { SessionServiceInterface } from "@natera/platform/lib/service/session";
import { apolloCache } from "./apolloCache";

// UPP-5665: two mounts of the same schema. The private mount serves every
// operation, including the public ones, so a signed-in patient never touches
// the public mount and never meets the aggressive WAF limit applied to it.
const PRIVATE_GRAPHQL_PATH = "/graphql";
const PUBLIC_GRAPHQL_PATH = "/public/graphql";

// Code the private mount's auth gate puts in its 401 body. Matching on it rather
// than on the bare status keeps an edge or proxy 401 from logging the user out.
const UNAUTHENTICATED_CODE = "UNAUTHENTICATED";

type AuthGateNetworkError = {
  statusCode?: number;
  result?: { errors?: { extensions?: { code?: string } }[] };
};

export default class UppClient {
  private client: ApolloClient<NormalizedCacheObject>;
  private logoutInFlight: Promise<void> | null = null;

  constructor(
    readonly configService: ConfigServiceInterface,
    readonly sessionService: SessionServiceInterface<{
      uid: string;
      sub: string;
    }>
  ) {}

  public getApolloClient = async (): Promise<
    ApolloClient<NormalizedCacheObject>
  > => {
    const apolloServiceLink = this.getServerLink();
    const errorLink = this.getErrorLink();

    this.client = new ApolloClient({
      cache: apolloCache,
      link: from([errorLink, apolloServiceLink]),
    });

    return this.client;
  };

  private getServerLink = () => {
    const getAuthorizedFetch = async (uri: string, options: RequestInit) => {
      const token = await this.sessionService.getToken();

      const isExpired =
        !token?.expiresAt || Date.now() / 1000 - token.expiresAt > 0;

      if (!isExpired && token?.accessToken) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${token.accessToken}`,
        };
      }

      // Routing is decided per request rather than per operation, so a login or
      // logout takes effect on the next request without rebuilding the client.
      // An expired token still routes privately: that preserves the existing
      // INVALID_ACCESS_TOKEN response that drives the auto-logout below, which a
      // generic denial from the public mount would not.
      return fetch(
        token?.accessToken ? PRIVATE_GRAPHQL_PATH : PUBLIC_GRAPHQL_PATH,
        options
      );
    };

    return new HttpLink({
      uri: PRIVATE_GRAPHQL_PATH,
      fetch: getAuthorizedFetch,
    });
  };

  private getErrorLink = () =>
    onError(({ graphQLErrors, networkError }) => {
      const hasInvalidAccessToken = Boolean(
        graphQLErrors?.some(
          ({ extensions }) =>
            extensions?.code === ResultCodes.INVALID_ACCESS_TOKEN
        )
      );

      // The private mount rejects tokenless callers before Apollo runs, so that
      // rejection arrives as a 401 network error rather than a GraphQL error.
      // An expired session routes here rather than to the public mount
      // precisely so it gets logged out instead of silently wedging.
      const gateError = networkError as AuthGateNetworkError | null;
      const isRejectedByAuthGate =
        gateError?.statusCode === 401 &&
        Boolean(
          gateError.result?.errors?.some(
            ({ extensions }) => extensions?.code === UNAUTHENTICATED_CODE
          )
        );

      if (!hasInvalidAccessToken && !isRejectedByAuthGate) {
        return;
      }

      if (this.logoutInFlight) {
        return;
      }

      this.logoutInFlight = this.sessionService.logout().finally(() => {
        this.logoutInFlight = null;
      });
    });
}
